import { writable, type Writable } from 'svelte/store';
import { browser } from '$app/environment';
import type { SvelteComponent } from 'svelte';
import type { Api_AccountConnection } from '$lib/types/api/graphql';

/**
 * Debounce a function for a set amount of time.
 * @param {function} callback - Function to debounce
 * @param {number} wait - Amount of time to debounce the function
 */
export function debounce(callback: Function, wait: number) {
  let lastArgs, lastThis;
  let result;
  let timeoutId = null;

  function invoke() {
    const args = lastArgs;
    const curArg = lastThis;
    lastArgs = lastThis = null;

    result = callback.apply(curArg, args);
    return result;
  }

  function cancel() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = lastThis = null;
  }

  function flush() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
      if (lastArgs) return invoke();
      lastArgs = lastThis = null;
    }
    return result;
  }

  function debounced(...args) {
    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;

    clearTimeout(timeoutId);
    timeoutId = setTimeout(invoke, wait);

    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

export function debouncePromise(callback: any, wait: number) {
  let timeoutId: NodeJS.Timeout | null = null;
  return (...args: any[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    return new Promise(resolve => {
      timeoutId = setTimeout(() => resolve(callback(...args)), wait);
    });
  };
}

/**
 * use to delay the execution inside an async function
 * @param {number} ms - The amount of time to delay the execution of the function.
 * @returns {Promise<void>} A promise that resolves after the specified time has passed.
 * @example
 * async function example() {
 *   await delay(1000);
 * }
 */
export async function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function throttle(fn, interval) {
  let lastExecutionTime = 0;
  let timerId;

  return function (...args) {
    const now = Date.now();
    if (now - lastExecutionTime >= interval) {
      fn.apply(this, args);
      lastExecutionTime = now;
    } else {
      clearTimeout(timerId);
      timerId = setTimeout(
        () => {
          fn.apply(this, args);
          lastExecutionTime = Date.now();
        },
        interval - (now - lastExecutionTime)
      );
    }
  };
}

export function toggleBodyScroll(enabled: boolean) {
  if (browser && document.body) {
    document.body.classList.toggle('overflow-hidden', !enabled);
  }
}

/**
 * `matchMedia` wraps the native `window.matchMedia` API, exposing the `matches` property as a
 * Svelte store that updates whenever the value changes, and provides an easy clean-up function.
 */
export function matchMedia(query: string): [Writable<boolean>, () => void] {
  const matches = writable(false);

  if (!browser) {
    return [
      matches,
      () => {
        /*noop*/
      }
    ];
  }

  const mediaQuery = window.matchMedia(query);
  matches.set(mediaQuery ? mediaQuery.matches : false);

  const update = ({ matches: m }) => {
    matches.set(m);
  };
  mediaQuery.addEventListener('change', update);
  const cleanup = () => {
    mediaQuery.removeEventListener('change', update);
  };
  return [matches, cleanup];
}

/**
 * Returns the data of a cookie within the browser.
 */
export function getCookie(name: string) {
  const cookie = {};
  document.cookie.split(';').forEach(function (el) {
    const [k, v] = el.split('=');
    cookie[k.trim()] = v;
  });
  return cookie[name];
}

export function sortArrayOfObjects(items, getter) {
  const copy = JSON.parse(JSON.stringify(items));

  const sortFn = fn => {
    copy.sort((a, b) => {
      a = fn(a);
      b = fn(b);
      return a === b ? 0 : a < b ? -1 : 1;
    });
  };

  getter.forEach(x => {
    const fn = typeof x === 'function' ? x : item => item[x];
    sortFn(fn);
  });

  return copy;
}

export function lazyComponent<T extends SvelteComponent>(
  func: () => Promise<{ default: new (...args: any[]) => T }>
): () => Promise<new (...args: any[]) => T> {
  return () =>
    new Promise(async (resolve, reject) => {
      try {
        const klass = await func();
        return resolve(klass.default);
      } catch (error) {
        reject(error);
      }
    });
}

// All connection types follow the same interface
export function extractNodes<T = any>(conn: any) {
  return (conn?.edges || []).map(e => e?.node) as T[];
}

export function withHttps(url: string) {
  return url.replace(/^(?:(.*:)?\/\/)?(.*)/i, (match, schema, noneSchemaUrl) =>
    schema ? match : `https://${noneSchemaUrl}`
  );
}
